import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Observable, catchError, map, of, switchMap, take } from 'rxjs';
import { FilterService } from 'src/app/modules/live-chat/services/filter.service';
import { environment } from '../../../environments/environment';
import { FeatureToggles } from '../enums/feature-toggles.enum';
import { generateRequestTimeLogHeader } from '../interceptors/log.interceptor';
import { CompanyService } from './company.service';
import { UserService } from './user.service';

type FeatureToggleCompanies = {
  companiesIds: string[];
  featureName: string;
  id: number;
};
@Injectable({
  providedIn: 'root',
})
export class FeatureToggleService {
  private findByNameCache = new Map<string, FeatureToggleCompanies>();

  constructor(
    private httpClient: HttpClient,
    private userService: UserService,
    private companyService: CompanyService,
    private injector: Injector
  ) {}

  findByName(
    featureToggleName: string,
    companiesIds: string[]
  ): Observable<FeatureToggleCompanies | null> {
    if (!featureToggleName) return of(null);

    const cachedResult = this.findByNameCache.get(featureToggleName);

    if (cachedResult) {
      return of(cachedResult);
    }

    return this.httpClient
      .get<FeatureToggleCompanies>(
        `${environment.externalUrls.bff}/feature-toggle/by-name/${featureToggleName}`,
        {
          params: {
            companiesIds: companiesIds.join(','),
          },
        }
      )
      .pipe(
        map((result) => {
          this.findByNameCache.set(featureToggleName, result);
          return result;
        })
      );
  }

  companyCanAccessFromCache(
    featureName: string,
    options: { onlySelectedCompanies?: boolean } = {
      onlySelectedCompanies: false,
    }
  ): boolean {
    let companiesIds: string[] | undefined = [];
    if (this.userService.isCurrentUserGlobal()) {
      companiesIds = this.filterService?.filterState?.companyIds;
    } else {
      companiesIds = options?.onlySelectedCompanies
        ? this.filterService?.filterState?.companyIds
        : this.companyService
            .getCompanies()
            ?.map((company) => company.companyId);
    }

    const feature = this.findByNameCache.get(featureName);
    if (!feature) return false;
    if (!feature.companiesIds) return true;

    return (
      !feature.companiesIds.length ||
      !!companiesIds?.some((companyId) =>
        feature.companiesIds.includes(companyId)
      )
    );
  }

  /**
   * Loads the toggle into the cache without depending on the user's companies or filter,
   * so it can later be evaluated synchronously for any company.
   */
  preload(featureName: FeatureToggles): void {
    this.findByName(featureName, [])
      .pipe(
        take(1),
        catchError(() => of(null))
      )
      .subscribe();
  }

  isLoadedInCache(featureName: string): boolean {
    return this.findByNameCache.has(featureName);
  }

  companyIdsCanAccessFromCache(companiesIds: string[], featureName: string): boolean {
    const feature = this.findByNameCache.get(featureName);
    if (!feature) return false;
    if (!feature.companiesIds) return true;

    return (
      !feature.companiesIds.length ||
      companiesIds.some((companyId) => feature.companiesIds.includes(companyId))
    );
  }

  private get filterService(): FilterService {
    return this.injector.get(FilterService); //TO AVOID CYCLIC DEPENDENCY
  }

  someCompanyCanAccess(
    featureName: FeatureToggles,
    options: { onlySelectedCompanies?: boolean } = {
      onlySelectedCompanies: false,
    }
  ): Observable<boolean> {
    return this.filterService.filterState$.pipe(
      switchMap((filterState) => {
        let companiesIds: string[] | undefined = [];
        if (this.userService.isCurrentUserGlobal()) {
          companiesIds = filterState?.companyIds;
        } else {
          companiesIds = options?.onlySelectedCompanies
            ? filterState?.companyIds
            : this.companyService
                .getCompanies()
                ?.map((company) => company.companyId);
        }
        if (!companiesIds || !companiesIds.length) {
          return of(false);
        }
        return this.companyCanAccess(companiesIds, featureName);
      })
    );
  }

  companyCanAccess(companiesIds: string[], featureName: string) {
    return this.findByName(featureName, companiesIds).pipe(
      map((feature) => {
        if (!feature) return false;

        if (!feature.companiesIds) return true;

        return (
          !feature.companiesIds.length ||
          companiesIds.some((companyId) =>
            feature.companiesIds.includes(companyId)
          )
        );
      }),
      catchError(() => of(false))
    );
  }
}
